import React, { useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ControllerChips, ControllerDropdownInput, ControllerEditor, ControllerNumberInput, ControllerTextInput } from "components/basic/controller-inputs/ControllerInputs";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { MaintenanceDto, MaintenanceEmail, MaintenanceStatus, TimeUnit, useGetCoordinatedMaintenanceByProjectIdQuery, useTestEmailLazyQuery } from "gql/generated/types";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "primereact/selectitem";
import { TabPanel, TabView } from "primereact/tabview";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";
import { z } from "zod";
import useProjectContext from "../../hooks/useProjectContext";
import { maintenanceStatuses } from "../../utils/const";
import { statuses } from "../../utils/types";
import { apolloErrorToaster, errorToaster, renderTag } from "../../utils/utils";
import EmailTextInfo from "./EmailTextInfo";

type MaintenanceEmailFormProps = {
  setEmails: React.Dispatch<React.SetStateAction<MaintenanceEmail[]>>;
  email?: MaintenanceEmail;
  closeDialog?: () => void;
};

const hours = [1, 2, 3, 4, 6, 12];
const minutes = [1, 2, 3, 4, 5, 6, 10, 15, 20, 30];
const units: SelectItem[] = [
  { value: TimeUnit.Days, label: T("time.days") },
  { value: TimeUnit.Hours, label: T("time.hours") },
  { value: TimeUnit.Minutes, label: T("time.minutes") },
];

const requiredFieldsMessage = { message: T("word.required-fields") };

const schema = z.object({
  title: z.string(requiredFieldsMessage).nonempty(requiredFieldsMessage),
  triggerFrom: z.enum(statuses),
  triggerTo: z.enum(statuses),
  header: z.string(requiredFieldsMessage).nonempty(requiredFieldsMessage),
  recipientsTo: z
    .array(
      z.string(requiredFieldsMessage).email({ message: T("alert.invalid-email") }),
      requiredFieldsMessage
    )
    .nonempty(requiredFieldsMessage),
  recipientsCc: z.optional(z.array(z.string(requiredFieldsMessage).email())),
  text: z.string(requiredFieldsMessage).nonempty(requiredFieldsMessage),
  repeatPeriodTime: z.optional(
    z.object({
      value: z.number(requiredFieldsMessage).nonnegative(),
      unit: z.enum(["MINUTES", "HOURS", "DAYS"], requiredFieldsMessage),
      enabled: z.boolean(),
    })
  ),
  reminderBeforeDisconnect: z.optional(
    z.object({
      hours: z.number(requiredFieldsMessage).nonnegative(),
      enabled: z.boolean(),
    })
  ),
});

const MaintenanceEmailForm: React.FC<MaintenanceEmailFormProps> = ({ setEmails, email, closeDialog }) => {
  const [maintenance, setMaintenance] = useState<MaintenanceDto | null>(null);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [periodicUnit, setPeriodicUnit] = useState<"MINUTES" | "HOURS" | "DAYS">(email?.repeatPeriodTime?.unit ?? "DAYS");
  const [toStatus, setToStatus] = useState<MaintenanceStatus>(email?.triggerTo ?? MaintenanceStatus.Coordinated);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<MaintenanceEmail>({
    defaultValues: email,
    resolver: zodResolver(schema),
  });

  const { projectId } = useProjectContext();

  const {data: coordData, loading} = useGetCoordinatedMaintenanceByProjectIdQuery({
    variables: {
      id: projectId!
    },
    skip: !projectId,
    onError: apolloErrorToaster
  })

  const [test, {loading: testLoading}] = useTestEmailLazyQuery();

  const coordMaintenances = useMemo(() => coordData?.coordinatedByProjectId ?? [], [coordData?.coordinatedByProjectId]);

  const errorsLength = Object.keys(errors).length;

  const onSubmit = (data: MaintenanceEmail) => {
    if (email) {
      setEmails(prev => prev.map(e => (e.id === email.id ? { ...email, ...data } : e)));
    } else {
      const defaultFields: Partial<MaintenanceEmail> = {
        repeatPeriodTime: {
          value: 1,
          unit: TimeUnit.Days,
          enabled: false,
        },
        reminderBeforeDisconnect: {
          hours: 72,
          enabled: false,
        },
      }
      setEmails(prev => [...prev, {...defaultFields, ...data, id: v4()}]);
    }
    closeDialog?.();
  };

  const sendTestEmail = (data: MaintenanceEmail) => {
    if (!maintenance) {
      errorToaster(T("alert.coordinated-maintenance-not-selected"));
      return;
    }
    test({
      variables: {
        maintenance,
        email: {...data, id: v4()}
      }
    })
  }

  const handleDuplicate = () => {
    if (!email) return;
    setEmails(prev => [...prev, {...email, id: v4()}]);
    closeDialog?.();
  }

  return (
    <form className="flex flex-column maintenance-email-form" onSubmit={handleSubmit(onSubmit)}>
      <TabView panelContainerClassName="p-0 md:p-4">
        <TabPanel header={T("word.main")} contentStyle={{ minHeight: "40rem" }}>
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={12} />
            )}
          />
          <div className="flex flex-wrap">
            <InputLabelWrapper labelName={T("word.email-trigger")} col={6}>
              <div className="p-inputgroup">
                <Controller
                  name="triggerFrom"
                  defaultValue={MaintenanceStatus.Registered}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      value={field.value}
                      options={maintenanceStatuses}
                      optionLabel="translationKey"
                      onChange={e => field.onChange(e.value)}
                      itemTemplate={opt => renderTag({ status: opt.value })}
                      valueTemplate={opt => opt && renderTag({ status: opt.value })}
                      className="status-dropdown"
                    />
                  )}
                />
                <span className="p-inputgroup-addon p-1">⟶</span>
                <Controller
                  name="triggerTo"
                  defaultValue={MaintenanceStatus.Coordinated}
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      value={field.value}
                      options={maintenanceStatuses}
                      optionLabel="translationKey"
                      onChange={e => {
                        field.onChange(e.value);
                        setToStatus(e.value);
                      }}
                      itemTemplate={opt => renderTag({ status: opt.value })}
                      valueTemplate={opt => opt && renderTag({ status: opt.value })}
                      className="status-dropdown"
                    />
                  )}
                />
              </div>
            </InputLabelWrapper>
            <Controller
              name="header"
              control={control}
              render={({ field }) => (
                <ControllerTextInput
                  field={field}
                  errors={errors}
                  label={T("word.header")}
                  col={6}
                />
              )}
            />
          </div>
          <Controller
            name="recipientsTo"
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <ControllerChips
                field={field}
                errors={errors}
                label={`${T("word.recipients")} [To]`}
                col={12}
                separator=", "
              />
            )}
          />
          <Controller
            name="recipientsCc"
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <ControllerChips
                field={field}
                errors={errors}
                label={`${T("word.recipients")} [Cc]`}
                col={12}
              />
            )}
          />
          <Accordion>
            <AccordionTab header={T("word.email-notification-frequency")}>
              <Controller
                name="repeatPeriodTime.enabled"
                control={control}
                render={({ field }) => (
                  <label className="flex gap-2 align-items-center w-full md:w-12 p-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={e => {
                        field.onChange(e.checked);
                      }}
                      name={field.name}
                    />
                    {T("label.enabled-periodic-email-notifications")}
                  </label>
                )}
              />
              <div className="flex gap-3">
                <Controller
                  name="repeatPeriodTime.value"
                  defaultValue={1}
                  control={control}
                  render={({ field }) => (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                      {periodicUnit === "DAYS" ? (
                        <ControllerNumberInput
                          field={field}
                          errors={errors}
                          label={T("word.value")}
                          col={3}
                        />
                      ) : (
                        <ControllerDropdownInput
                          field={field}
                          errors={errors}
                          label={T("word.value")}
                          options={periodicUnit === "HOURS" ? hours : minutes}
                          col={3}
                        />
                      )}
                    </>
                  )}
                />
                <Controller
                  name="repeatPeriodTime.unit"
                  defaultValue={TimeUnit.Days}
                  control={control}
                  render={({ field }) => (
                    <ControllerDropdownInput
                      field={field}
                      errors={errors}
                      label={T("word.unit")}
                      options={units}
                      onChange={e => {
                        field.onChange(e.value);
                        setPeriodicUnit(e.value);
                        setValue("repeatPeriodTime.value", 1);
                      }}
                      col={4}
                    />
                  )}
                />
              </div>
            </AccordionTab>
            {toStatus === MaintenanceStatus.InProgress && (
              <AccordionTab header={T("label.email-notification")}>
                <Controller
                  name="reminderBeforeDisconnect.enabled"
                  defaultValue={false}
                  control={control}
                  render={({ field }) => (
                    <label className="flex gap-2 align-items-center w-full md:w-12 p-2">
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={e => {
                          field.onChange(e.checked);
                        }}
                        name={field.name}
                      />
                      {T("label.enable-email-notification-till-disconnection")}
                    </label>
                  )}
                />
                <Controller
                  name="reminderBeforeDisconnect.hours"
                  defaultValue={72}
                  control={control}
                  render={({ field }) => (
                    <ControllerNumberInput
                      field={field}
                      errors={errors}
                      label={T("time.hours")}
                      col={3}
                    />
                  )}
                />
              </AccordionTab>
            )}
          </Accordion>
        </TabPanel>
        <TabPanel header={T("word.template")} contentStyle={{ minHeight: "40rem" }}>
          <Controller
            name="text"
            control={control}
            render={({ field }) => (
              <ControllerEditor
                field={field}
                errors={errors}
                label={
                  <>
                    {T("word.text")}
                    <i
                      className="ml-2 cursor-pointer pi pi-info-circle"
                      onClick={() => setOpenInfoDialog(true)}
                    />
                  </>
                }
                col={12}
              />
            )}
          />
        </TabPanel>
      </TabView>
      <div className="flex flex-wrap justify-content-between mt-3 px-2 gap-2">
        <div className="p-inputgroup w-25rem">
          <Dropdown
            value={maintenance}
            options={coordMaintenances}
            optionLabel="title"
            placeholder={T("word.select-one")}
            emptyMessage={T("mdm.coordinated-maintenance-not-found")}
            onChange={e => setMaintenance(e.value)}
          />
          <Button
            label={T("label.test-email")}
            title={T("message.testing-email-for-coordinated-maintenance")}
            className="p-inputgroup-addon"
            onClick={handleSubmit(sendTestEmail)}
            loading={loading || testLoading}
            severity="warning"
          />
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            title={T("action.duplicate")}
            icon="pi pi-clone"
            severity="warning"
            outlined
            onClick={handleDuplicate}
          />
          <Button
            label={email ? T("action.update") : T("action.add")}
            severity={email ? "info" : "success"}
            badge={errorsLength > 0 ? errorsLength.toString() : undefined}
            badgeClassName={errorsLength > 0 ? "p-badge-danger" : ""}
          />
        </div>
      </div>
      {errorsLength > 0 && (
        <small className="p-error text-right p-2">{T("message.fill-required-fields")}</small>
      )}
      <Dialog
        header={T("mdm.maintenance-model")}
        visible={openInfoDialog}
        onHide={() => setOpenInfoDialog(false)}
      >
        <EmailTextInfo />
      </Dialog>
    </form>
  );
};

export default MaintenanceEmailForm;
